import { FormControl, Grid, RadioGroup, Radio, FormControlLabel, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { InformationDialog, OutlinedButton, Select, Switch } from '../../../../lib/components';
import { useStepper } from '../../partials/actionStepperPartial';
import { useCreateCustomBenefit } from '../hooks/useCreateCustomBenefit';
import { BenefitEnum } from '@pxtra-client-lib-legacy/gen';
import { useSnackbar } from 'notistack';
import { useStore } from '../../../../hooks/useStore';
import { FeedbackAlert } from '../../../../lib/components/atomic/feedback';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { useGetAvailableConstraints } from '../../../../queries/custom';
import { supportEmail } from '../../../../misc/globals';
import { extractApiErrorMessage } from '../../../../lib/util/apiErrorMessage';

export const BenefitLogic = observer(() => {
  const {
    newCustomBenefit,
    createCustomBenefit: create,
    patchCustomBenefit: patch
  } = useCreateCustomBenefit();
  const { companyStore } = useStore();
  const { data: availableConstraints } = useGetAvailableConstraints(
    companyStore.company?.id ?? '',
    newCustomBenefit.editingBenefitId
  );
  const { t } = useTranslation();
  const { setNextButtonConfig, activeStep, setActiveStep, showInfoDrawer, setShowInfoDrawer } =
    useStepper();
  const regularPageExitRef = useRef(false);
  const snackbar = useSnackbar();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const isEditing = newCustomBenefit.editingActiveBenefit;

  const benefitOptions = useMemo(() => {
    if (!availableConstraints) return [];
    return availableConstraints.map((b: BenefitEnum) => ({
      value: b,
      text: t(`benefits:${b}`)
    }));
  }, [availableConstraints, t]);

  const handleAskSupport = () => {
    window.open(`mailto:${supportEmail}`, '_blank');
  };

  const handleConfirmCreate = async () => {
    const previousStatus = newCustomBenefit.status;
    try {
      newCustomBenefit.status = 'active';
      const request = newCustomBenefit.editingBenefitId ? patch : create;
      await request(newCustomBenefit.editingBenefitId || '');
      regularPageExitRef.current = true;
      setShowCreateDialog(false);
      setActiveStep(activeStep + 1);
    } catch (error) {
      newCustomBenefit.status = previousStatus;
      snackbar.enqueueSnackbar({
        message: extractApiErrorMessage(error) ?? t('custom:error'),
        variant: 'error'
      });
    }
  };

  useEffect(() => {
    newCustomBenefit.backButtonClicked = false;
    return () => {
      if (!regularPageExitRef.current && !newCustomBenefit.backButtonClicked) {
        newCustomBenefit.clear();
      }
    };
  }, []);

  useEffect(() => {
    setNextButtonConfig({
      text: isEditing ? t('common:SAVE') : t('custom:create'),
      endIcon: undefined,
      onClick: () => {
        if (newCustomBenefit.influenceBenefit && !newCustomBenefit.influencedBenefit) {
          const errorMessage = t('custom:error_missing_influenced_benefit');
          snackbar.enqueueSnackbar({ message: errorMessage, variant: 'error' });
        } else {
          setShowCreateDialog(true);
        }
      }
    });
  }, [isEditing, t]);

  return (
    <>
      <Grid container spacing={1} alignItems="flex-end">
        <Grid item xs={12} md={12}>
          <FormControl>
            <Switch
              label={t('custom:influence')}
              labelPlacement={'start'}
              name="influence"
              checked={newCustomBenefit.influenceBenefit}
              onChange={(event, checked) => {
                newCustomBenefit.influenceBenefit = checked;
              }}
              disabled={newCustomBenefit.partialEditingOnly}
              boldLabel={true}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} md={5}>
          <Select
            label={'Benefit'}
            futureEditingLocked={true}
            disabled={!newCustomBenefit.influenceBenefit || newCustomBenefit.partialEditingOnly}
            options={benefitOptions}
            value={newCustomBenefit.influencedBenefit}
            onChange={(e) => (newCustomBenefit.influencedBenefit = e.target.value as BenefitEnum)}
            variant={'standard'}
          />
        </Grid>
        <Grid item xs={12} md={6.5}>
          <RadioGroup
            name="influenceType"
            row
            defaultValue={newCustomBenefit.constraintType}
            sx={{ marginLeft: '15px', marginBottom: '5px' }}
          >
            <FormControlLabel
              value="replace"
              control={<Radio />}
              label={t('custom:replace')}
              disabled={!newCustomBenefit.influenceBenefit || newCustomBenefit.partialEditingOnly}
              onChange={() => (newCustomBenefit.constraintType = 'replace')}
            />
            <FormControlLabel
              value="limit"
              control={<Radio />}
              label={t('custom:limit')}
              disabled={!newCustomBenefit.influenceBenefit || newCustomBenefit.partialEditingOnly}
              onChange={() => (newCustomBenefit.constraintType = 'limit')}
            />
          </RadioGroup>
        </Grid>
        <Grid item xs={12} md={5}>
          {newCustomBenefit.influenceBenefit &&
            (newCustomBenefit.influencedBenefit === ('fahrtkosten' as BenefitEnum) ||
              newCustomBenefit.influencedBenefit === ('öpnv_tickets' as BenefitEnum)) && (
              <>
                <FormControl>
                  <Switch
                    label={t('custom:replace_mobility_constraint', {
                      benefit:
                        newCustomBenefit.influencedBenefit === 'fahrtkosten'
                          ? t('benefits:öpnv_tickets')
                          : t('benefits:fahrtkosten')
                    })}
                    labelPlacement={'end'}
                    name="mobilityConstraint"
                    checked={!newCustomBenefit.allowBothMobilityBenefits}
                    onChange={(event, checked) => {
                      newCustomBenefit.allowBothMobilityBenefits = !checked;
                    }}
                    disabled={newCustomBenefit.partialEditingOnly}
                    boldLabel={true}
                  />
                </FormControl>
                <div>
                  <FeedbackAlert
                    title={t('custom:constraint_hint_mobility_title')}
                    description={[
                      t('custom:constraint_hint_mobility', {
                        benefit:
                          benefitOptions.find(
                            (option) => option.value === newCustomBenefit.influencedBenefit
                          )?.text ?? '',
                        otherBenefit:
                          newCustomBenefit.influencedBenefit === 'fahrtkosten'
                            ? t('benefits:öpnv_tickets')
                            : t('benefits:fahrtkosten')
                      })
                    ]}
                    severity="info"
                    bulletPoints={false}
                  />
                </div>
              </>
            )}
        </Grid>
      </Grid>
      <div className="mt-6">
        <FeedbackAlert
          severity="info"
          description={
            <Trans i18nKey="custom:instant_available" components={{ bold: <strong /> }} />
          }
          actionElement={
            <OutlinedButton
              size="small"
              onClick={handleAskSupport}
              text={t('custom:ask_support')}
              color={'inherit'}
            />
          }
        />
      </div>
      <InformationDialog
        open={showCreateDialog}
        onClose={() => {
          setShowCreateDialog(false);
          setAccepted(false);
        }}
        onConfirm={handleConfirmCreate}
        onCancel={() => {
          setShowCreateDialog(false);
          setAccepted(false);
        }}
        title={isEditing ? t('custom:edit_dialog_title') : t('custom:create_dialog_title')}
        confirmButtonText={isEditing ? t('common:SAVE') : t('custom:create_now')}
        cancelButtonText={t('common:CANCEL')}
        confirmButtonDisabled={!accepted}
        content={
          <div className="flex flex-col">
            <Typography variant="body2" className="pb-4 text-[14px]" color="primary.main">
              <Trans
                i18nKey={isEditing ? 'custom:edit_dialog_text' : 'custom:create_dialog_text'}
                values={{ name: newCustomBenefit.name_de ?? '' }}
                components={{ bold: <strong /> }}
              />
            </Typography>
            <div className="flex items-start gap-8 rounded-xl bg-grey-50 p-4">
              <Switch
                className="mx-0"
                checked={accepted}
                onChange={(_event, checked) => setAccepted(checked)}
                inputProps={{ 'aria-label': t('custom:legalHint') }}
              />
              <Typography
                variant="body2"
                className="cursor-pointer text-[14px]"
                color={accepted ? 'primary.main' : 'grey.500'}
                onClick={() => setAccepted((prev) => !prev)}
              >
                <Trans i18nKey="custom:legalHintRich" components={{ bold: <strong /> }} />
              </Typography>
            </div>
          </div>
        }
      />

      <Drawer
        label={t('custom:drawer_logic_headline')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('custom:drawer_logic_influence_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'custom:drawer_logic_influence_text'}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('custom:drawer_logic_replace_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_logic_replace_text'} components={{ bold: <strong /> }} />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('custom:drawer_logic_limit_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_logic_limit_text'} components={{ bold: <strong /> }} />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('custom:drawer_logic_example_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_logic_example_text'} components={{ bold: <strong /> }} />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('custom:drawer_logic_limitation_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans
              i18nKey={'custom:drawer_logic_limitation_text'}
              components={{ bold: <strong /> }}
            />
          </Typography>
          <Typography variant={'body2'} fontWeight={'bolder'} sx={{ marginBottom: '0.3rem' }}>
            {t('custom:drawer_logic_export_headline')}
          </Typography>
          <Typography paragraph variant={'body2'}>
            <Trans i18nKey={'custom:drawer_logic_export_text'} components={{ bold: <strong /> }} />
          </Typography>
        </>
      </Drawer>
    </>
  );
});
